/**
 * Client-facing analytics ingestion contract for `POST /analytics/events`.
 *
 * There is deliberately no `userId` anywhere in this file. The stored user id is resolved server-side
 * from the Cognito sub by `withUserIdMiddleware`, so route code typed against these types has no
 * property to read or forward. The trusted Lambda-to-Lambda variant that may carry a `userId` lives in
 * `LambdaInvocationTypes` instead.
 */

export type AnalyticsClientPlatform = 'web' | 'ios' | 'android';

export type AnalyticsEventSession = {
    sessionId: string;
    /** ISO-8601 UTC timestamp. */
    startTimestamp?: string;
    /** ISO-8601 UTC timestamp. */
    endTimestamp?: string;
};

export type AnalyticsEventInput = {
    /** UUID minted by the producer; retained for investigation and future deduplication. */
    eventId: string;
    eventType: string;
    /** ISO-8601 UTC timestamp of when the event occurred on the client. */
    eventTimestamp: string;
    platform: AnalyticsClientPlatform;
    session?: AnalyticsEventSession;
    appVersion?: string;
    appBuild?: string;
    osVersion?: string;
    /** Client software identity — on web, the browser name and version. Never a hardware identifier. */
    deviceMake?: string;
    deviceModel?: string;
    attributes?: Record<string, string>;
};

export type RecordAnalyticsEventsRequest = {
    events: AnalyticsEventInput[];
};

/**
 * `accepted` = handed to Firehose (delivery to S3 is asynchronous and not confirmed here).
 * `rejected` = permanently failed after the bounded retry. Telemetry delivery is best-effort, so this
 * is reported in a 202 body rather than as an error status.
 */
export type RecordAnalyticsEventsResponse = {
    accepted: number;
    rejected: number;
};
